import React, { useState, useEffect } from 'react'
import { Modal, Input, Button, ConfigProvider, App } from 'antd'
import { LockOutlined } from '@ant-design/icons'
import axios from 'axios'
import zhCN from 'antd/locale/zh_CN.js'
import type { Locale } from 'antd/lib/locale/index.js'
import { appApiUrls } from '../../shared/url-map.js'

interface AdminAuthProps {
  children: React.ReactNode
}

function AdminAuthContent({ children }: AdminAuthProps) {
  const { message } = App.useApp()
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)

  // 检查登录状态
  const checkAuthStatus = async () => {
    try {
      const response = await axios.get(appApiUrls.auth.check)
      if (response.data.success && response.data.isAdmin) {
        setIsAuthenticated(true)
      } else {
        setIsAuthenticated(false)
        setIsModalVisible(true)
      }
    } catch (error) {
      console.error('检查登录状态失败:', error)
      setIsAuthenticated(false)
      setIsModalVisible(true)
    }
  }

  // 登录验证
  const handleLogin = async () => {
    if (!password.trim()) {
      message.error('请输入密码')
      return
    }

    setLoading(true)
    try {
      const response = await axios.post(appApiUrls.auth.login, { password })
      if (response.data.success) {
        message.success('登录成功')
        setIsAuthenticated(true)
        setIsModalVisible(false)
        setPassword('')
      }
    } catch (error: any) {
      // 密码错误时清空输入框
      setPassword('')

      // 显示具体的错误信息
      if (error.response?.status === 401) {
        message.error('密码错误，请重新输入')
      } else if (error.response?.status === 400) {
        message.error('密码不能为空')
      } else {
        const errorMessage = error.response?.data?.message || '登录失败，请稍后重试'
        message.error(errorMessage)
      }

      console.error('登录失败:', error)
    } finally {
      setLoading(false)
    }
  }

  // 处理键盘事件
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin()
    }
  }

  useEffect(() => {
    checkAuthStatus()
  }, [])

  // 已认证，显示子组件
  if (isAuthenticated) {
    return <>{children}</>
  }

  // 未认证或正在检查登录状态，显示登录界面
  return (
    <>
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="text-center">
          {isAuthenticated === null ? (
            <div className="mb-4 text-lg">正在验证权限...</div>
          ) : (
            <>
              <LockOutlined className="mb-4 text-6xl text-gray-400" />
              <h1 className="mb-2 text-2xl font-bold text-gray-700">管理员权限验证</h1>
              <p className="text-gray-500">请输入管理员密码以继续访问</p>
            </>
          )}
        </div>
      </div>

      <Modal
        title="管理员登录"
        open={isModalVisible}
        onCancel={() => {
          // 不允许关闭弹窗，必须验证通过
        }}
        footer={null}
        closable={false}
        maskClosable={false}
        width={400}
      >
        <div className="py-4">
          <div className="mb-4 text-center">
            <LockOutlined className="mb-2 text-4xl text-blue-500" />
            <p className="text-gray-600">请输入管理员密码</p>
          </div>

          <Input.Password
            placeholder="请输入密码"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            size="large"
            className="mb-4"
            autoFocus
          />

          <Button type="primary" size="large" block loading={loading} onClick={handleLogin}>
            登录
          </Button>
        </div>
      </Modal>
    </>
  )
}

export default function AdminAuth({ children }: AdminAuthProps) {
  return (
    <ConfigProvider locale={zhCN as unknown as Locale}>
      <App>
        <AdminAuthContent>{children}</AdminAuthContent>
      </App>
    </ConfigProvider>
  )
}
